/**
 * Mock retrieval service — the public, unauthenticated journey.
 *
 * This mock is where the prototype most has to imitate the real security
 * behaviour, because the retrieval contract is a security property, not a
 * feature:
 *
 *   - Failures are indistinguishable. Unknown phrase, wrong PIN, disabled,
 *     expired and deleted all resolve to the SAME `{ ok: false }`. The mock has
 *     no branch that could leak which one occurred.
 *   - It never rejects with a reason. Callers get a uniform result they cannot
 *     use to probe.
 *   - Neither argument is logged, stored, or placed in a URL.
 *
 * The real timing side channel (constant-time comparison, uniform latency) is a
 * Phase 4/5 concern noted in docs/security-model.md; the mock cannot solve it,
 * but it must not design it away by short-circuiting.
 */

import { makeDemoProfiles } from "./fixtures.js";

/**
 * The one phrase/PIN pair that "works" in the prototype.
 *
 * Four words and six digits, matching the real generator (§8) so the mock
 * exercises the same shapes the server produces. Not a real credential — §8
 * forbids using an example phrase as a production one.
 */
const DEMO_PHRASE = "harbour-willow-lantern-compass";
const DEMO_PIN = "246810";

/**
 * @returns {import("../interfaces.js").RetrievalService}
 */
export function createMockRetrievalService() {
  return {
    async retrieve(phrase, pin) {
      const matches = phrase.trim().toLowerCase() === DEMO_PHRASE && pin.trim() === DEMO_PIN;

      // One exit for failure, carrying nothing. Do not add a reason here.
      if (!matches) {
        return { ok: false };
      }

      const contacts = makeDemoProfiles()[0]?.contacts ?? [];
      return { ok: true, contacts };
    },
  };
}
